(() => {
  'use strict';

  const ROOT_ID = 'zv-cookie-consent';
  const PREVIEW_PARAM = 'zv_cookie_preview';
  const PREFERENCES_PARAM = 'zv_cookie_preferences';
  const API_TIMEOUT = 8000;
  const CONSENT_SAVE_TIMEOUT = 6000;
  const RETRY_DELAY = 30000;
  const LOCAL_CHOICE_KEY = 'zv_cookie_choice_recorded';
  const LOCAL_CONSENT_KEY = 'zv_cookie_consent_fallback';

  let root;
  let api;
  let activePanel;
  let previousFocus = null;
  let openedFromFooter = false;
  let hasRecordedChoice = false;
  let apiLoadPromise = null;
  let pendingSyncTimer = null;
  let localConsentMemory = null;

  const selectors = {
    intro: '[data-zv-cookie-panel="intro"]',
    preferences: '[data-zv-cookie-panel="preferences"]',
    more: '[data-zv-cookie-more]',
    acceptAll: '[data-zv-cookie-accept-all]',
    rejectAll: '[data-zv-cookie-reject-all]',
    save: '[data-zv-cookie-save]',
    analytics: '[data-zv-cookie-analytics]',
    marketing: '[data-zv-cookie-marketing]',
    personalization: '[data-zv-cookie-personalization]',
    status: '[data-zv-cookie-status]'
  };

  const getPanel = (name) => root?.querySelector(selectors[name]);

  function clearPreviewParameters() {
    const url = new URL(window.location.href);
    const hadPreviewParameter = url.searchParams.has(PREVIEW_PARAM) || url.searchParams.has(PREFERENCES_PARAM);

    if (!hadPreviewParameter) return;

    url.searchParams.delete(PREVIEW_PARAM);
    url.searchParams.delete(PREFERENCES_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  function getCurrentConsent() {
    try {
      return api?.currentVisitorConsent?.() || {};
    } catch (error) {
      console.warn('[ZinVolt cookies] Kon huidige toestemming niet lezen.', error);
      return {};
    }
  }

  function hasExistingChoice(consent) {
    return ['analytics', 'marketing', 'preferences'].some(
      (key) => consent?.[key] === 'yes' || consent?.[key] === 'no'
    );
  }

  function normalizeConsentValues(values = {}) {
    const marketing = Boolean(values.marketing);

    return {
      // Marketingmeting gebruikt dezelfde acquisitie- en sessiecontext als
      // analytics. Een expliciete marketingkeuze trekt analytics daarom mee.
      analytics: Boolean(values.analytics) || marketing,
      marketing,
      // Advertentiepersonalisatie kan nooit ruimer zijn dan marketing.
      preferences: marketing && Boolean(values.preferences)
    };
  }

  function readLocalConsent() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(LOCAL_CONSENT_KEY) || 'null');
      if (!stored || typeof stored !== 'object') return localConsentMemory;

      localConsentMemory = {
        values: normalizeConsentValues(stored.values),
        pending: Boolean(stored.pending),
        savedAt: Number(stored.savedAt) || 0
      };
      return localConsentMemory;
    } catch (error) {
      return localConsentMemory;
    }
  }

  function writeLocalConsent(values, pending = false) {
    const normalizedValues = normalizeConsentValues(values);
    localConsentMemory = {
      values: normalizedValues,
      pending: Boolean(pending),
      savedAt: Date.now()
    };

    try {
      window.localStorage.setItem(
        LOCAL_CONSENT_KEY,
        JSON.stringify(localConsentMemory)
      );
      window.localStorage.setItem(LOCAL_CHOICE_KEY, '1');
    } catch (error) {
      // Shopify Customer Privacy blijft de primaire bron zodra die beschikbaar is.
    }
  }

  function hasLocalRecordedChoice() {
    if (readLocalConsent()) return true;

    try {
      return window.localStorage.getItem(LOCAL_CHOICE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function loadPrivacyApi() {
    if (api?.setTrackingConsent) return Promise.resolve(api);
    if (apiLoadPromise) return apiLoadPromise;

    apiLoadPromise = new Promise((resolve, reject) => {
      const startedAt = Date.now();

      function attempt() {
        if (window.Shopify?.customerPrivacy) {
          resolve(window.Shopify.customerPrivacy);
          return;
        }

        if (window.Shopify?.loadFeatures) {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (error) {
                reject(error);
                return;
              }

              if (window.Shopify?.customerPrivacy) {
                resolve(window.Shopify.customerPrivacy);
              } else {
                reject(new Error('Shopify Customer Privacy API is niet beschikbaar.'));
              }
            }
          );
          return;
        }

        if (Date.now() - startedAt >= API_TIMEOUT) {
          reject(new Error('Shopify Customer Privacy API kon niet worden geladen.'));
          return;
        }

        window.setTimeout(attempt, 100);
      }

      attempt();
    }).catch((error) => {
      apiLoadPromise = null;
      throw error;
    });

    return apiLoadPromise;
  }

  function applyConsentToApi(values) {
    const normalizedValues = normalizeConsentValues(values);

    return new Promise((resolve, reject) => {
      if (!api?.setTrackingConsent) {
        reject(new Error('Shopify Customer Privacy API is niet beschikbaar.'));
        return;
      }

      let completed = false;
      const timeout = window.setTimeout(() => {
        if (completed) return;
        completed = true;
        reject(new Error('Shopify Customer Privacy API reageerde niet op tijd.'));
      }, CONSENT_SAVE_TIMEOUT);

      try {
        api.setTrackingConsent(normalizedValues, () => {
          if (completed) return;
          completed = true;
          window.clearTimeout(timeout);
          resolve(normalizedValues);
        });
      } catch (error) {
        if (completed) return;
        completed = true;
        window.clearTimeout(timeout);
        reject(error);
      }
    });
  }

  function syncPreferenceControls() {
    const consent = getCurrentConsent();
    const fallback = readLocalConsent();
    const recordedInShopify = hasExistingChoice(consent);
    const analytics = root.querySelector(selectors.analytics);
    const marketing = root.querySelector(selectors.marketing);
    const personalization = root.querySelector(selectors.personalization);

    hasRecordedChoice = recordedInShopify || Boolean(fallback) || hasLocalRecordedChoice();

    const values = recordedInShopify
      ? {
          analytics: consent.analytics === 'yes',
          marketing: consent.marketing === 'yes',
          preferences: consent.preferences === 'yes'
        }
      : fallback?.values;

    // No recorded choice is not consent. Opening Preferences therefore
    // always starts with every non-essential category disabled.
    const normalizedValues = values ? normalizeConsentValues(values) : null;
    if (analytics) analytics.checked = normalizedValues ? normalizedValues.analytics : false;
    if (marketing) marketing.checked = normalizedValues ? normalizedValues.marketing : false;
    if (personalization) personalization.checked = normalizedValues ? normalizedValues.preferences : false;
  }

  function bindPreferenceDependencies() {
    const analytics = root.querySelector(selectors.analytics);
    const marketing = root.querySelector(selectors.marketing);
    const personalization = root.querySelector(selectors.personalization);

    analytics?.addEventListener('change', () => {
      if (analytics.checked) return;
      if (marketing) marketing.checked = false;
      if (personalization) personalization.checked = false;
    });

    marketing?.addEventListener('change', () => {
      if (marketing.checked) {
        if (analytics) analytics.checked = true;
        return;
      }
      if (personalization) personalization.checked = false;
    });

    personalization?.addEventListener('change', () => {
      if (!personalization.checked) return;
      if (marketing) marketing.checked = true;
      if (analytics) analytics.checked = true;
    });
  }

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
  }

  function setPanel(name) {
    const intro = getPanel('intro');
    const preferences = getPanel('preferences');

    intro.hidden = name !== 'intro';
    preferences.hidden = name !== 'preferences';
    activePanel = name === 'intro' ? intro : preferences;

    if (name === 'preferences') {
      syncPreferenceControls();
    }

    window.requestAnimationFrame(() => {
      activePanel?.focus({ preventScroll: true });
    });
  }

  function openConsent(panelName = 'intro', fromFooter = false) {
    if (!root) return;

    previousFocus = document.activeElement;
    openedFromFooter = fromFooter;
    root.hidden = false;
    document.documentElement.classList.add('zv-cookie-lock');
    setPanel(panelName);
  }

  function closeConsent() {
    if (!root) return;

    root.hidden = true;
    getPanel('intro').hidden = true;
    getPanel('preferences').hidden = true;
    document.documentElement.classList.remove('zv-cookie-lock');
    activePanel = null;
    openedFromFooter = false;

    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus({ preventScroll: true });
    }
  }

  function setSaving(isSaving) {
    const buttons = root.querySelectorAll(
      `${selectors.more}, ${selectors.acceptAll}, ${selectors.rejectAll}, ${selectors.save}`
    );
    buttons.forEach((button) => {
      button.disabled = isSaving;
      if (!button.dataset.originalText) button.dataset.originalText = button.textContent.trim();
      button.textContent = isSaving ? (root.dataset.labelSaving || 'Even opslaan…') : button.dataset.originalText;
    });
  }

  function setStatus(message = '') {
    root.querySelectorAll(selectors.status).forEach((status) => {
      status.textContent = message;
    });
  }

  function updateGoogleConsent(values, pendingSync = false) {
    const normalizedValues = normalizeConsentValues(values);
    const analyticsConsent = normalizedValues.analytics ? 'granted' : 'denied';
    const marketingConsent = normalizedValues.marketing ? 'granted' : 'denied';
    const personalizationConsent = normalizedValues.marketing && normalizedValues.preferences
      ? 'granted'
      : 'denied';
    const consentState = {
      analytics_storage: analyticsConsent,
      ad_storage: marketingConsent,
      ad_user_data: marketingConsent,
      ad_personalization: personalizationConsent
    };

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('consent', 'update', consentState);

    window.zvConsentState = {
      ...normalizedValues,
      google: consentState,
      pendingSync: Boolean(pendingSync)
    };

    window.dataLayer.push({
      event: 'zv_consent_update',
      zv_analytics_consent: analyticsConsent,
      zv_marketing_consent: marketingConsent,
      zv_preferences_consent: normalizedValues.preferences ? 'granted' : 'denied',
      zv_ad_personalization_consent: personalizationConsent,
      zv_consent_pending_sync: Boolean(pendingSync)
    });

    updateClarityConsent(consentState);
  }

  function updateClarityConsent(consentState) {
    window.zvClarityConsentState = {
      ad_Storage: consentState.ad_storage,
      analytics_Storage: consentState.analytics_storage
    };

    const applyClarityConsent = () => {
      if (typeof window.clarity !== 'function') return false;

      window.clarity('consentv2', window.zvClarityConsentState);
      return true;
    };

    // GTM kan Clarity pas na deze voorkeurupdate laden. Deze publieke helper
    // laat zowel de korte retries hieronder als een GTM-tag exact dezelfde
    // toestemmingsstatus toepassen, ook wanneer toestemming wordt ingetrokken.
    window.zvApplyClarityConsent = applyClarityConsent;

    if (applyClarityConsent()) return;

    [250, 1000, 3000].forEach((delay) => {
      window.setTimeout(applyClarityConsent, delay);
    });
  }

  function dispatchConsentUpdate(values, pendingSync = false) {
    updateGoogleConsent(values, pendingSync);
    document.dispatchEvent(
      new CustomEvent('zv:cookie-consent-updated', {
        detail: { ...normalizeConsentValues(values), pendingSync }
      })
    );
  }

  async function syncPendingConsent() {
    const fallback = readLocalConsent();
    if (!fallback?.pending) return true;

    try {
      api = await loadPrivacyApi();
      await applyConsentToApi(fallback.values);
      writeLocalConsent(fallback.values, false);
      dispatchConsentUpdate(fallback.values, false);
      return true;
    } catch (error) {
      console.warn('[ZinVolt cookies] Lokale keuze kon nog niet met Shopify worden gesynchroniseerd.', error);
      return false;
    }
  }

  function schedulePendingConsentSync(delay = RETRY_DELAY) {
    if (pendingSyncTimer || !readLocalConsent()?.pending) return;

    pendingSyncTimer = window.setTimeout(async () => {
      pendingSyncTimer = null;
      const synced = await syncPendingConsent();
      if (!synced) schedulePendingConsentSync(RETRY_DELAY);
    }, delay);
  }

  async function saveConsent(values) {
    const normalizedValues = normalizeConsentValues(values);
    setSaving(true);
    setStatus('');

    try {
      if (!api?.setTrackingConsent) {
        api = await loadPrivacyApi();
      }

      await applyConsentToApi(normalizedValues);
      writeLocalConsent(normalizedValues, false);
      hasRecordedChoice = true;
      clearPreviewParameters();
      dispatchConsentUpdate(normalizedValues, false);
      setSaving(false);
      closeConsent();
    } catch (error) {
      console.warn('[ZinVolt cookies] Shopify was tijdelijk niet beschikbaar. De keuze is lokaal bewaard.', error);
      writeLocalConsent(normalizedValues, true);
      hasRecordedChoice = true;
      clearPreviewParameters();
      dispatchConsentUpdate(normalizedValues, true);
      setSaving(false);
      closeConsent();
      schedulePendingConsentSync(5000);
    }
  }

  function injectFooterLink() {
    if (document.getElementById('zv-cookie-footer-link')) return;

    const button = document.createElement('button');
    button.id = 'zv-cookie-footer-link';
    button.type = 'button';
    button.className = 'zv-cookie-footer-link';
    button.textContent = root.dataset.labelFooter || 'Cookievoorkeuren aanpassen';
    button.setAttribute('data-zv-cookie-open', '');

    const policyList = document.querySelector('.footer__policies');
    if (policyList) {
      const item = document.createElement('li');
      item.className = 'zv-cookie-footer-item';
      item.appendChild(button);
      policyList.appendChild(item);
      return;
    }

    const copyright = document.querySelector('.footer__copyright');
    if (copyright) {
      copyright.appendChild(button);
      return;
    }

    const footer = document.querySelector('footer');
    footer?.appendChild(button);
  }

  function bindEvents() {
    bindPreferenceDependencies();

    root.querySelector(selectors.more)?.addEventListener('click', () => {
      setPanel('preferences');
    });

    root.querySelector(selectors.acceptAll)?.addEventListener('click', () => {
      saveConsent({ analytics: true, marketing: true, preferences: true });
    });

    root.querySelector(selectors.rejectAll)?.addEventListener('click', () => {
      saveConsent({ analytics: false, marketing: false, preferences: false });
    });

    root.querySelector(selectors.save)?.addEventListener('click', () => {
      const analytics = Boolean(root.querySelector(selectors.analytics)?.checked);
      const marketing = Boolean(root.querySelector(selectors.marketing)?.checked);
      const personalization = Boolean(root.querySelector(selectors.personalization)?.checked);

      saveConsent({
        analytics,
        marketing,
        preferences: personalization
      });
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zv-cookie-open]');
      if (!trigger) return;
      event.preventDefault();
      openConsent('preferences', true);
    });

    document.addEventListener('keydown', (event) => {
      if (root.hidden || !activePanel) return;

      if (event.key === 'Escape' && openedFromFooter && hasRecordedChoice) {
        closeConsent();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(activePanel);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('online', () => schedulePendingConsentSync(0));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') schedulePendingConsentSync(1000);
    });
    document.addEventListener('shopify:section:load', injectFooterLink);
  }

  async function init() {
    root = document.getElementById(ROOT_ID);
    if (!root) return;

    bindEvents();
    injectFooterLink();

    const initialLocalConsent = readLocalConsent();
    if (initialLocalConsent) {
      updateGoogleConsent(initialLocalConsent.values, initialLocalConsent.pending);
    }

    const params = new URLSearchParams(window.location.search);
    const forcePreview = params.get(PREVIEW_PARAM) === '1';
    const forcePreferences = params.get(PREFERENCES_PARAM) === '1';

    if (forcePreview || forcePreferences) {
      clearPreviewParameters();
    }

    try {
      api = await loadPrivacyApi();
      const pendingBeforeSync = Boolean(readLocalConsent()?.pending);
      const pendingSynced = await syncPendingConsent();

      const consent = getCurrentConsent();
      const recordedInShopify = hasExistingChoice(consent);
      hasRecordedChoice = recordedInShopify || (pendingBeforeSync && pendingSynced);

      if (recordedInShopify) {
        const shopifyConsentValues = {
          analytics: consent.analytics === 'yes',
          marketing: consent.marketing === 'yes',
          preferences: consent.preferences === 'yes'
        };
        writeLocalConsent(shopifyConsentValues, false);
        dispatchConsentUpdate(shopifyConsentValues, false);
      }

      if (forcePreferences) {
        openConsent('preferences', true);
      } else if (forcePreview) {
        openConsent('intro');
      } else if (!hasRecordedChoice && api.shouldShowBanner?.()) {
        openConsent('intro');
      }
    } catch (error) {
      console.warn('[ZinVolt cookies] Shopify privacykoppeling is tijdelijk niet beschikbaar.', error);

      const localChoiceExists = hasLocalRecordedChoice();
      hasRecordedChoice = localChoiceExists;

      if (forcePreferences || forcePreview) {
        openConsent(forcePreferences ? 'preferences' : 'intro', forcePreferences);
        setStatus(root.dataset.labelPreviewUnavailable || 'Voorbeeldmodus: de Shopify privacykoppeling is nog niet beschikbaar.');
      } else if (!localChoiceExists) {
        openConsent('intro');
      } else {
        schedulePendingConsentSync(5000);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
